import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../hooks/useAuth'
import {
  getAllDeviceEventsForNamespace,
  type DeviceEventData,
  getAlarms,
  type Alarm,
  acknowledgeEscalation,
  resolveEscalation,
  type AlarmResolution
} from '../../services/api'
import { OCAColors, Typography, Spacing, BorderRadius } from '../../theme/colors'
import { IOSListSection } from '../ui/IOSListSection'
import { IOSListItem } from '../ui/IOSListItem'
import { MaterialIcon } from '../ui/MaterialIcon'
import { format, sameDay, addDay } from '@formkit/tempo'
import { AlarmResolveModal } from '../AlarmResolveModal'

// Enhanced types for alarm management
interface ActiveEscalation {
  id: string
  alertId: string
  alertType: string
  alertSeverity?: string
  deviceId: string
  deviceName?: string
  locationId?: string
  locationPath?: string
  policyId: string
  currentLevel: number
  maxLevelReached: number
  alertStartedAt: string
  nextEscalationAt?: string
  lastNotificationAt?: string
  isAcknowledged: boolean
  acknowledgedBy?: string
  acknowledgedAt?: string
  acknowledgmentMethod?: string
  acknowledgmentNote?: string
  isResolved: boolean
  resolvedAt?: string
  resolvedBy?: string
  resolutionNote?: string
  alertData: any
  createdAt: string
}

// interface EscalationMetrics {
//   period: string
//   totalAlerts: number
//   escalatedAlerts: number
//   avgResponseTime: number
//   avgResolutionTime: number
//   notificationsSent: number
//   successRate: number
// }

export const AlarmsPage: React.FC = () => {
  const { getAccessToken, isAuthenticated, user } = useAuth()
  const [events, setEvents] = useState<DeviceEventData[]>([])
  const [activeFilter, setActiveFilter] = useState<'escalations' | 'alarms' | 'logs'>('escalations')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [lastFetchTime, setLastFetchTime] = useState<Date | null>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Enhanced alarm management state
  const [activeEscalations, setActiveEscalations] = useState<ActiveEscalation[]>([])
  const [alarms, setAlarms] = useState<Alarm[]>([])
  const [actioningAlarms, setActioningAlarms] = useState<Set<string>>(new Set())

  // Resolve modal state
  const [resolveModalOpen, setResolveModalOpen] = useState(false)
  const [selectedEscalation, setSelectedEscalation] = useState<ActiveEscalation | null>(null)

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData()
      startPolling()
    }

    return () => {
      stopPolling()
    }
  }, [isAuthenticated])

  // Load all alarm-related data
  const loadAllData = async () => {
    if (!isAuthenticated) return

    try {
      setIsLoading(true)
      setError('')

      console.log('🚨 AlarmsPage: Loading all alarm data...')

      // Load different data based on active filter
      if (activeFilter === 'escalations') {
        await loadEscalations()
      } else if (activeFilter === 'alarms') {
        await loadAlarms()
      } else {
        await loadEvents()
      }

    } catch (err) {
      console.error('🚨 AlarmsPage: Error loading data:', err)
      setError('Failed to load alarm data')
    } finally {
      setIsLoading(false)
    }
  }

  // Load escalations - simulated since we don't have the full escalation API in mobile
  const loadEscalations = async () => {
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        throw new Error('Unable to get access token')
      }

      // Get namespace ID from user context
      const namespaceId = user?.namespace?.id
      if (!namespaceId) {
        console.warn('⚠️ No namespace ID available for loading escalations')
        setActiveEscalations([])
        return
      }

      // For now, we'll simulate escalations based on active alarms
      const alarmsData = await getAlarms(accessToken, undefined, namespaceId)
      console.log('🚨 AlarmsPage: Loaded', alarmsData.length, 'alarms for escalation analysis from namespace', namespaceId)

      // Transform active alarms into escalations
      const activeEscalationsData: ActiveEscalation[] = alarmsData
        .filter(alarm => alarm.status === 'active' || alarm.status === 'acknowledged')
        .map(alarm => ({
          id: `escalation-${alarm.id}`,
          alertId: alarm.id,
          alertType: 'PRESSURE_ALARM',
          alertSeverity: 'HIGH', // alarm.severity not available in current API
          deviceId: alarm.deviceId,
          deviceName: `Device ${alarm.deviceId.slice(-4)}`,
          locationId: undefined,
          locationPath: 'Unknown Location',
          policyId: 'default-policy',
          currentLevel: alarm.status === 'acknowledged' ? 1 : 0,
          maxLevelReached: alarm.status === 'acknowledged' ? 1 : 0,
          alertStartedAt: alarm.timestamp,
          nextEscalationAt: alarm.status === 'active' ? new Date(Date.now() + 15 * 60 * 1000).toISOString() : undefined,
          lastNotificationAt: alarm.timestamp,
          isAcknowledged: alarm.status === 'acknowledged',
          acknowledgedBy: alarm.status === 'acknowledged' ? 'Mobile User' : undefined,
          acknowledgedAt: alarm.status === 'acknowledged' ? new Date().toISOString() : undefined,
          acknowledgmentMethod: 'mobile',
          acknowledgmentNote: undefined,
          isResolved: false,
          resolvedAt: undefined,
          resolvedBy: undefined,
          resolutionNote: undefined,
          alertData: {
            message: alarm.message,
            deviceId: alarm.deviceId,
            threshold: undefined, // alarm.thresholdValue not available in current API
            value: undefined // alarm.actualValue not available in current API
          },
          createdAt: alarm.timestamp
        }))

      setActiveEscalations(activeEscalationsData)
      console.log('🚨 AlarmsPage: Created', activeEscalationsData.length, 'escalations from real alarm data')

    } catch (err) {
      console.error('🚨 AlarmsPage: Error loading escalations:', err)
      throw err
    }
  }

  // Load alarms
  const loadAlarms = async () => {
    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        throw new Error('Unable to get access token')
      }

      // Get namespace ID from user context
      const namespaceId = user?.namespace?.id
      if (!namespaceId) {
        console.warn('⚠️ No namespace ID available for loading alarms')
        setAlarms([])
        return
      }

      const alarmsData = await getAlarms(accessToken, undefined, namespaceId)
      console.log('🚨 AlarmsPage: Loaded', alarmsData.length, 'alarms from namespace', namespaceId)
      setAlarms(alarmsData)
    } catch (err) {
      console.error('🚨 AlarmsPage: Error loading alarms:', err)
      throw err
    }
  }

  // Reload data when filter changes
  useEffect(() => {
    if (isAuthenticated) {
      loadAllData()
    }
  }, [activeFilter])

  // Alarm action handlers
  const handleAcknowledge = async (escalation: ActiveEscalation) => {
    if (!isAuthenticated) return

    const escalationId = escalation.id
    setActioningAlarms(prev => new Set(prev).add(escalationId))

    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        throw new Error('Unable to get access token')
      }

      console.log(`✅ Acknowledging escalation ${escalationId}`)
      const success = await acknowledgeEscalation(escalationId, accessToken)

      if (success) {
        // Reload escalations to get updated state
        await loadEscalations()
      } else {
        throw new Error('Failed to acknowledge escalation')
      }
    } catch (err) {
      console.error('❌ Error acknowledging escalation:', err)
      setError('Failed to acknowledge alarm')
    } finally {
      setActioningAlarms(prev => {
        const newSet = new Set(prev)
        newSet.delete(escalationId)
        return newSet
      })
    }
  }

  const handleResolve = (escalation: ActiveEscalation) => {
    setSelectedEscalation(escalation)
    setResolveModalOpen(true)
  }

  const handleResolveSubmit = async (resolution: AlarmResolution) => {
    if (!selectedEscalation || !isAuthenticated) return

    const escalationId = selectedEscalation.id
    setActioningAlarms(prev => new Set(prev).add(escalationId))

    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        throw new Error('Unable to get access token')
      }

      console.log(`✅ Resolving escalation ${escalationId}`)
      const success = await resolveEscalation(escalationId, resolution, accessToken)

      if (success) {
        // Reload escalations to get updated state
        await loadEscalations()
        setResolveModalOpen(false)
        setSelectedEscalation(null)
      } else {
        throw new Error('Failed to resolve escalation')
      }
    } catch (err) {
      console.error('❌ Error resolving escalation:', err)
      setError('Failed to resolve alarm')
      throw err // Re-throw so modal can handle error
    } finally {
      setActioningAlarms(prev => {
        const newSet = new Set(prev)
        newSet.delete(escalationId)
        return newSet
      })
    }
  }

  // Alarm action functions (commented out since actions are removed from UI)
  /*
  const handleAlarmAction = async (alarmId: string, action: 'acknowledge' | 'mute' | 'escalate' | 'resolve') => {
    if (!token || !user) return

    setActioningAlarms(prev => new Set(prev).add(alarmId))

    try {
      console.log(`🚨 AlarmsPage: ${action} alarm ${alarmId}`)

      // Simulate API call since we don't have full escalation APIs in mobile yet
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Update local state to reflect the action
      if (activeFilter === 'escalations') {
        setActiveEscalations(prev => prev.map(escalation => {
          if (escalation.alertId === alarmId) {
            const now = new Date().toISOString()
            const userName = user.name || user.email || 'Mobile User'

            switch (action) {
              case 'acknowledge':
                return {
                  ...escalation,
                  isAcknowledged: true,
                  acknowledgedBy: userName,
                  acknowledgedAt: now,
                  acknowledgmentMethod: 'mobile',
                  currentLevel: Math.max(escalation.currentLevel, 1)
                }
              case 'resolve':
                return {
                  ...escalation,
                  isResolved: true,
                  resolvedBy: userName,
                  resolvedAt: now
                }
              default:
                return escalation
            }
          }
          return escalation
        }).filter(escalation => action === 'resolve' ? escalation.alertId !== alarmId : true))
      }

      if (activeFilter === 'alarms') {
        setAlarms(prev => prev.map(alarm => {
          if (alarm.id === alarmId) {
            switch (action) {
              case 'acknowledge':
                return { ...alarm, status: 'acknowledged' as const }
              case 'mute':
                return { ...alarm, status: 'muted' as const }
              case 'resolve':
                return { ...alarm, status: 'resolved' as const }
              default:
                return alarm
            }
          }
          return alarm
        }).filter(alarm => action === 'resolve' ? alarm.id !== alarmId : true))
      }

    } catch (err) {
      console.error(`🚨 AlarmsPage: Error ${action} alarm:`, err)
      setError(`Failed to ${action} alarm`)
    } finally {
      setActioningAlarms(prev => {
        const newSet = new Set(prev)
        newSet.delete(alarmId)
        return newSet
      })
    }
  }
  */

  const startPolling = () => {
    // Poll for updates every 30 seconds
    pollIntervalRef.current = setInterval(() => {
      if (isAuthenticated) {
        if (activeFilter === 'logs' && lastFetchTime) {
          loadNewEvents()
        } else {
          // Refresh current view
          loadAllData()
        }
      }
    }, 30000) // 30 seconds
  }

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const loadEvents = async () => {
    if (!isAuthenticated) return

    try {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        throw new Error('Unable to get access token')
      }

      const deviceEvents = await getAllDeviceEventsForNamespace(accessToken, 100)
      console.log('📋 Loaded device events:', deviceEvents.length)

      setEvents(deviceEvents)
      setLastFetchTime(new Date())
    } catch (err) {
      console.error('Error loading events:', err)
      throw err
    }
  }

  const loadNewEvents = async () => {
    if (!isAuthenticated || !lastFetchTime) return
    
    try {
      const token = await getAccessToken()
      if (!token) return
      
      // Fetch events created after our last fetch time
      const deviceEvents = await getAllDeviceEventsForNamespace(token, 50)
      const newEvents = deviceEvents.filter(event => 
        new Date(event.timestamp) > lastFetchTime
      )
      
      if (newEvents.length > 0) {
        console.log('📋 Found new events:', newEvents.length)
        setEvents(prevEvents => {
          // Merge and deduplicate by eventId, keeping newest first
          const allEvents = [...newEvents, ...prevEvents]
          const uniqueEvents = allEvents.filter((event, index, self) => 
            index === self.findIndex(e => e.eventId === event.eventId)
          )
          return uniqueEvents.sort((a, b) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
        })
      }
      
      setLastFetchTime(new Date())
    } catch (err) {
      console.error('Error loading new events:', err)
    }
  }

  // Filter logic based on active tab
  const filteredEvents = events.filter(_event => {
    if (activeFilter !== 'logs') return true
    // When on logs tab, show all events
    return true
  })

  // Get escalation status helpers
  const getEscalationStatusColor = (escalation: ActiveEscalation) => {
    if (escalation.isResolved) return OCAColors.success
    if (escalation.isAcknowledged) return OCAColors.warning
    return OCAColors.danger
  }

  const getEscalationStatusIcon = (escalation: ActiveEscalation) => {
    if (escalation.isResolved) return 'check_circle'
    if (escalation.isAcknowledged) return 'check'
    return 'error'
  }

  const getAlarmStatusColor = (alarm: Alarm) => {
    switch (alarm.status) {
      case 'active': return OCAColors.danger
      case 'acknowledged': return OCAColors.warning
      case 'muted': return OCAColors.gray500
      case 'resolved': return OCAColors.success
      default: return OCAColors.textSecondary
    }
  }

  const getAlarmStatusIcon = (alarm: Alarm) => {
    switch (alarm.status) {
      case 'active': return 'error'
      case 'acknowledged': return 'check'
      case 'muted': return 'volume_off'
      case 'resolved': return 'check_circle'
      default: return 'help'
    }
  }

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diffMs = now.getTime() - time.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  const getEventColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'alarm':
      case 'error':
        return OCAColors.deviceAlarm
      case 'warning':
        return OCAColors.deviceWarning
      case 'info':
        return OCAColors.primary
      default:
        return OCAColors.gray500
    }
  }

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: OCAColors.secondaryLabel,
        fontSize: Typography.fontSize.base,
      }}>
        Loading events...
      </div>
    )
  }

  // Debug logging for active filter
  console.log('🚨 AlarmsPage: Active filter:', activeFilter)
  console.log('🚨 AlarmsPage: Data counts - Escalations:', activeEscalations.length, 'Alarms:', alarms.length, 'Events:', events.length)

  const formatEventTime = (timestamp: string): string => {
    const eventDate = new Date(timestamp)
    const today = new Date()
    const yesterday = addDay(today, -1)
    
    if (sameDay(eventDate, today)) {
      return format(eventDate, 'HH:mm')
    } else if (sameDay(eventDate, yesterday)) {
      return 'Yesterday'
    } else {
      return format(eventDate, 'DD-MMM')
    }
  }

  const handleEventTap = () => {
    console.log('Tapped event')
  }

  return (
    <div style={{
      backgroundColor: '#F2F2F7',
      minHeight: '100vh',
      paddingBottom: '100px',
    }}>
      {/* iOS-style header with enhanced alarm management tabs */}
      <div style={{
        padding: Spacing.lg,
        backgroundColor: OCAColors.gray900,
        borderBottom: `0.5px solid ${OCAColors.separator}`,
        minHeight: '96px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: Spacing.sm,
          marginBottom: Spacing.md,
        }}>
          <img
            src="/OmniIQ_AppIcon_1024x1024.png"
            alt="OMNI IQ"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
            }}
          />
          <h1 style={{
            fontSize: Typography.fontSize['2xl'],
            fontWeight: Typography.fontWeight.bold,
            color: OCAColors.background,
            margin: 0,
          }}>
            Alarm Center
          </h1>
        </div>

        {/* Enhanced tab segmented control */}
        <div style={{
          display: 'flex',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          padding: '2px',
        }}>
          {[
            ['escalations', 'Escalations'],
            ['alarms', 'Alarms'],
            ['logs', 'Logs']
          ].map(([filter, label]) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter as any)}
              style={{
                flex: 1,
                padding: `${Spacing.xs} ${Spacing.sm}`,
                fontSize: Typography.fontSize.sm,
                fontWeight: Typography.fontWeight.medium,
                backgroundColor: activeFilter === filter ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                color: activeFilter === filter ? OCAColors.background : 'rgba(255, 255, 255, 0.6)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Status summary for escalations */}
        {activeFilter === 'escalations' && !isLoading && (
          <div style={{
            marginTop: Spacing.md,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: Spacing.sm,
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: Typography.fontSize.lg,
                fontWeight: Typography.fontWeight.bold,
                color: OCAColors.danger,
              }}>
                {activeEscalations.filter(e => !e.isAcknowledged && !e.isResolved).length}
              </div>
              <div style={{
                fontSize: Typography.fontSize.xs,
                color: OCAColors.secondaryLabel,
              }}>
                Active
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: Typography.fontSize.lg,
                fontWeight: Typography.fontWeight.bold,
                color: OCAColors.warning,
              }}>
                {activeEscalations.filter(e => e.isAcknowledged && !e.isResolved).length}
              </div>
              <div style={{
                fontSize: Typography.fontSize.xs,
                color: OCAColors.secondaryLabel,
              }}>
                Acknowledged
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: Typography.fontSize.lg,
                fontWeight: Typography.fontWeight.bold,
                color: OCAColors.success,
              }}>
                {activeEscalations.filter(e => e.isResolved).length}
              </div>
              <div style={{
                fontSize: Typography.fontSize.xs,
                color: OCAColors.secondaryLabel,
              }}>
                Resolved
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: Spacing.md,
          margin: Spacing.md,
          backgroundColor: OCAColors.danger + '15',
          borderRadius: '8px',
          color: OCAColors.danger,
          fontSize: Typography.fontSize.sm,
        }}>
          {error}
        </div>
      )}

      {/* Content based on active tab */}
      {activeFilter === 'escalations' && (
        <div>
          {activeEscalations.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: Spacing.xl,
              color: OCAColors.secondaryLabel,
              fontSize: Typography.fontSize.base,
            }}>
              No active escalations
            </div>
          ) : (
            <IOSListSection title={`${activeEscalations.length} ACTIVE ESCALATIONS`}>
              {activeEscalations.map((escalation) => {
                const isActioning = actioningAlarms.has(escalation.id)
                const isResolved = escalation.isResolved
                const isAcknowledged = escalation.isAcknowledged

                return (
                  <div
                    key={escalation.id}
                    style={{
                      backgroundColor: OCAColors.background,
                      borderBottom: `0.5px solid ${OCAColors.separator}`,
                      padding: Spacing.md,
                    }}
                  >
                    {/* Main content */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: Spacing.md,
                      marginBottom: isResolved ? 0 : Spacing.md,
                    }}>
                      {/* Icon */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '20px',
                        backgroundColor: getEscalationStatusColor(escalation),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <MaterialIcon name={getEscalationStatusIcon(escalation)} size={20} color="#FFFFFF" />
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: Typography.fontSize.base,
                          fontWeight: Typography.fontWeight.semibold,
                          color: OCAColors.label,
                          marginBottom: '4px',
                        }}>
                          {escalation.alertData.message || 'Pressure Alert'}
                        </div>
                        <div style={{
                          fontSize: Typography.fontSize.sm,
                          color: OCAColors.secondaryLabel,
                          marginBottom: '2px',
                        }}>
                          {escalation.deviceName || `Device ${escalation.deviceId.slice(-4)}`}
                        </div>
                        <div style={{
                          fontSize: Typography.fontSize.xs,
                          color: OCAColors.tertiaryLabel,
                        }}>
                          Level {escalation.currentLevel} • {formatTimeAgo(escalation.alertStartedAt)}
                          {escalation.nextEscalationAt && !isAcknowledged && (
                            <span> • Next: {formatTimeAgo(escalation.nextEscalationAt)}</span>
                          )}
                        </div>
                      </div>

                      {/* Status badge */}
                      <div style={{
                        padding: `4px ${Spacing.sm}`,
                        backgroundColor: `${getEscalationStatusColor(escalation)}20`,
                        borderRadius: BorderRadius.sm,
                        fontSize: Typography.fontSize.xs,
                        fontWeight: Typography.fontWeight.medium,
                        color: getEscalationStatusColor(escalation),
                        flexShrink: 0,
                      }}>
                        {isResolved ? 'Resolved' : isAcknowledged ? 'ACK' : 'Active'}
                      </div>
                    </div>

                    {/* Action buttons - only show if not resolved */}
                    {!isResolved && (
                      <div style={{
                        display: 'flex',
                        gap: Spacing.sm,
                      }}>
                        <button
                          onClick={() => handleAcknowledge(escalation)}
                          disabled={isAcknowledged || isActioning}
                          style={{
                            flex: 1,
                            padding: `${Spacing.sm} ${Spacing.md}`,
                            fontSize: Typography.fontSize.sm,
                            fontWeight: Typography.fontWeight.semibold,
                            color: isAcknowledged || isActioning ? OCAColors.tertiaryLabel : OCAColors.primary,
                            backgroundColor: isAcknowledged || isActioning ? OCAColors.backgroundTertiary : `${OCAColors.primary}10`,
                            border: `1px solid ${isAcknowledged || isActioning ? OCAColors.separator : OCAColors.primary}`,
                            borderRadius: BorderRadius.md,
                            cursor: isAcknowledged || isActioning ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseDown={(e) => {
                            if (!isAcknowledged && !isActioning) {
                              e.currentTarget.style.transform = 'scale(0.97)'
                            }
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                        >
                          {isActioning ? '...' : isAcknowledged ? '✓ Acknowledged' : 'ACK'}
                        </button>
                        <button
                          onClick={() => handleResolve(escalation)}
                          disabled={isActioning}
                          style={{
                            flex: 1,
                            padding: `${Spacing.sm} ${Spacing.md}`,
                            fontSize: Typography.fontSize.sm,
                            fontWeight: Typography.fontWeight.semibold,
                            color: '#FFFFFF',
                            backgroundColor: isActioning ? OCAColors.textTertiary : OCAColors.success,
                            border: 'none',
                            borderRadius: BorderRadius.md,
                            cursor: isActioning ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseDown={(e) => {
                            if (!isActioning) {
                              e.currentTarget.style.transform = 'scale(0.97)'
                            }
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                        >
                          {isActioning ? '...' : 'RESOLVE'}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </IOSListSection>
          )}
        </div>
      )}

      {activeFilter === 'alarms' && (
        <div>
          {alarms.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: Spacing.xl,
              color: OCAColors.secondaryLabel,
              fontSize: Typography.fontSize.base,
            }}>
              No alarms found
            </div>
          ) : (
            <IOSListSection title={`${alarms.length} ALARMS`}>
              {alarms.map((alarm) => (
                <IOSListItem
                  key={alarm.id}
                  icon={
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: getAlarmStatusColor(alarm),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <MaterialIcon name={getAlarmStatusIcon(alarm)} size={20} color={OCAColors.background} />
                    </div>
                  }
                  title={alarm.message}
                  subtitle={
                    <div>
                      <div>Device {alarm.deviceId.slice(-4)} • Pressure</div>
                      <div style={{
                        fontSize: Typography.fontSize.xs,
                        color: OCAColors.secondaryLabel,
                        marginTop: '2px'
                      }}>
                        <span>{formatTimeAgo(alarm.timestamp)}</span>
                      </div>
                    </div>
                  }
                  value={alarm.status.charAt(0).toUpperCase() + alarm.status.slice(1)}
                  onClick={() => console.log('Alarm tapped:', alarm.id)}
                  status={alarm.status.charAt(0).toUpperCase() + alarm.status.slice(1)}
                  statusColor={getAlarmStatusColor(alarm)}
                />
              ))}
            </IOSListSection>
          )}
        </div>
      )}

      {activeFilter === 'logs' && (
        <div>
          {/* Keep the existing logs functionality */}
          {filteredEvents.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: Spacing.xl,
              color: OCAColors.secondaryLabel,
              fontSize: Typography.fontSize.base,
            }}>
              No events found
            </div>
          ) : (
            <IOSListSection title={`${filteredEvents.length} EVENTS`}>
              <div style={{ backgroundColor: OCAColors.background }}>
                {filteredEvents.map((event) => (
                  <div
                    key={event.eventId}
                    onClick={() => handleEventTap()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: `${Spacing.sm} ${Spacing.lg}`,
                      borderBottom: `0.5px solid ${OCAColors.separator}`,
                      cursor: 'pointer',
                      minHeight: '60px',
                    }}
                  >
                    <div style={{
                      width: '6px',
                      height: '32px',
                      backgroundColor: getEventColor(event.level),
                      borderRadius: '3px',
                      marginRight: Spacing.md,
                      flexShrink: 0,
                    }} />
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}>
                      <div style={{
                        fontSize: Typography.fontSize.sm,
                        fontWeight: Typography.fontWeight.medium,
                        color: OCAColors.label,
                        wordBreak: 'break-word',
                        lineHeight: '1.3',
                      }}>
                        {event.message}
                      </div>
                      <div style={{
                        fontSize: Typography.fontSize.xs,
                        color: OCAColors.secondaryLabel,
                        wordBreak: 'break-word',
                        lineHeight: '1.2',
                      }}>
                        {event.deviceName || `Device ${event.deviceId.slice(-5)}`} ({event.locationName || 'Unknown Location'})
                      </div>
                    </div>
                    <div style={{
                      fontSize: Typography.fontSize.xs,
                      color: OCAColors.secondaryLabel,
                      marginLeft: Spacing.sm,
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                    }}>
                      {formatEventTime(event.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            </IOSListSection>
          )}
        </div>
      )}

      {/* Resolve Modal */}
      <AlarmResolveModal
        isOpen={resolveModalOpen}
        onClose={() => {
          setResolveModalOpen(false)
          setSelectedEscalation(null)
        }}
        escalationId={selectedEscalation?.id || ''}
        deviceName={selectedEscalation?.deviceName || `Device ${selectedEscalation?.deviceId.slice(-4)}` || 'Unknown'}
        onSubmit={handleResolveSubmit}
      />
    </div>
  )
}